/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type ReactElement } from 'react';
import { useSite } from '@salesforce/storefront-next-runtime/site-context';
import type { ShopperProducts, ShopperSearch } from '@/scapi';
import { cn } from '@/lib/utils';
import ProductPrice from '@/components/product-price';

type PriceableProduct = ShopperProducts.schemas['Product'] | ShopperSearch.schemas['ProductSearchHit'];

/**
 * The price shown on a {@link ProductTileCard}: the regular price, plus — when the product is on
 * sale — the original price struck through with the sale price in nutrient-orange, matching the
 * Figma card (UCR-2824). Wraps the shared `ProductPrice`, which derives regular/sale/list values
 * from SFCC pricebook data; the active currency comes from site context. The base text color is
 * inherited from the card's themed text area (so it flips with the card theme); only the sale price
 * overrides to orange.
 */
export default function TilePrice({
    product,
    className,
}: {
    product: PriceableProduct;
    className?: string;
}): ReactElement {
    const { currency } = useSite();
    return (
        <ProductPrice
            type="unit"
            product={product}
            currency={currency}
            hidePromo
            listPriceFirst
            className={cn('flex items-center gap-2 font-sans text-lg tracking-[-0.01em]', className)}
            // Both prices are 700 (`font-bold`), overriding CurrentPrice's default `font-semibold`.
            // The list (strikethrough) price inherits the card's themed text color (`text-inherit`
            // beats ListPrice's default `text-muted-foreground`); only the sale price turns orange.
            currentPriceProps={{ className: 'font-bold leading-none' }}
            listPriceProps={{ className: 'font-bold leading-none text-inherit line-through' }}
            currentPriceClassNameOnSale="text-bm-orange-500"
        />
    );
}
